/**
 * GR 결과 화면(총괄 DEV-I-007). 디자인 결과 화면 v0.1의 구성·배치를 쓰고, 총괄 결정이 다른 곳은 총괄 결정을 따른다.
 *  - pending은 '결과 대기'. 상태 문구는 총괄 결정 문구(result-model RESULT_HEADLINE)
 *  - 다시 확인에 실패하면 지난 정오·집계를 숨기고 오류와 다시 확인 단추(통신 실패와 서버 결과 대기를 구분)
 *  - 정답 보기·해설·근거 문제·단어장·일반 객관식 정오·전체 점수·정답률은 보이지 않는다
 * 상태는 App이 갖고, 이 함수는 상태로 화면 하나를 만든다(입력이 없어 다시 그려도 잃는 값이 없다).
 */
import type { AssignmentInfo, ItemsResponse } from '../core/types.js';
import { joinPoints, MARK_LABEL, MARK_WORD, markOf, RESULT_HEADLINE, summarize, type GradingResponse } from '../core/result-model.js';
import { h } from './dom.js';
import { formatKst } from './solve.js';

export type ResultPhase = 'loading' | 'ok' | 'error' | 'denied' | 'notfound' | 'notsubmitted' | 'account';

export interface ResultState {
  readonly assignmentId: string | null;
  readonly attemptId: string;
  phase: ResultPhase;
  /** 오류 원인: network(연결) · server(5xx 등) · attempt_mismatch · malformed · timeout(확인 제한 시간 초과, DEV-I-008) */
  errorCause: string | null;
  /** 이 화면에서 결과를 한 번이라도 받았는가(오류 문구: 불러오지 못함 / 다시 확인하지 못함) */
  everLoaded: boolean;
  grading: GradingResponse | null;
  /** 바뀐 지점 비교용: 이 화면(같은 시도)에서 마지막으로 받은 결과. 화면에는 보이지 않는다 */
  baseline: GradingResponse | null;
  items: ItemsResponse | null;
  itemsFailed: boolean;
  assignment: AssignmentInfo | null;
  changed: Set<string>;
  busy: boolean;
  /** 이 화면이 마지막으로 보낸 결과 요청 번호 */
  seq: number;
  checkedAt: number | null;
}

export function blankResult(assignmentId: string | null, attemptId: string): ResultState {
  return { assignmentId, attemptId, phase: 'loading', errorCause: null, everLoaded: false, grading: null, baseline: null, items: null, itemsFailed: false, assignment: null, changed: new Set(), busy: false, seq: 0, checkedAt: null };
}

export interface ResultHandlers { readonly refresh: () => void; readonly toList: () => void; readonly toInfo: (() => void) | null; readonly toEvidence?: () => void; readonly toReview?: () => void; readonly toDetail?: () => void }

const hhmm = (t: number) => { const d = new Date(t); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };

export function renderResult(S: ResultState, H: ResultHandlers): HTMLElement {
  const title = S.items?.release.title ?? S.assignment?.release.title ?? 'GR 결과';
  const root = h('div', { class: 'result', 'data-testid': 'result', 'data-phase': S.phase, 'data-att': S.attemptId });
  root.append(h('div', { class: 'ahead' }, h('span', { class: 'mark', 'aria-hidden': 'true' }), h('span', { class: 't', 'data-testid': 'result-release' }, title),
    h('button', { class: 'exit', 'data-testid': 'result-exit', onclick: H.toList }, '과제 목록')));
  const main = h('div', { class: 'rmain' });
  const side = h('aside', { class: 'rside', 'aria-label': '지점별 결과' });
  root.append(h('div', { class: 'body' }, h('div', { class: 'cols' }, main, side)));
  const a = S.assignment;
  main.append(h('div', { class: 'rhead' },
    h('b', { 'data-testid': 'result-assignment' }, a?.title ?? S.items?.assignment.title ?? ''),
    h('span', { 'data-testid': 'result-submitted' }, a?.progress.submittedAt ? `제출 완료 · ${formatKst(a.progress.submittedAt)} (한국 시간)` : '제출 완료')));
  const backButtons = () => [
    H.toInfo !== null ? h('button', { class: 'secondary', 'data-testid': 'result-to-info', onclick: H.toInfo }, '과제 정보로') : null,
    h('button', { class: 'secondary', 'data-testid': 'to-list', onclick: H.toList }, '과제 목록으로'),
  ];

  if (S.phase !== 'ok' || S.grading === null) {
    const errBody = S.errorCause === 'timeout'
      ? '다시 확인해 주세요. 결과 대기가 아니라 정해진 시간 안에 결과를 받아 오지 못한 상태예요.'
      : S.errorCause === 'network'
      ? '연결을 확인한 뒤 다시 확인해 주세요. 결과 대기가 아니라 결과를 받아 오지 못한 상태예요.'
      : '잠시 문제가 생겼어요. 다시 확인해 주세요. 결과 대기가 아니라 결과를 받아 오지 못한 상태예요.';
    const msg: Record<Exclude<ResultPhase, 'ok'>, [string, string | null]> = {
      loading: ['결과를 불러오고 있어요', null],
      // DEV-I-008: 확인 제한 시간 초과는 총괄 문구 "결과를 확인하지 못했어요. 다시 확인해 주세요."(제목 + 본문 첫 문장)
      error: [S.errorCause === 'timeout' ? '결과를 확인하지 못했어요.' : S.everLoaded ? '결과를 다시 확인하지 못했어요' : '결과를 불러오지 못했어요', errBody],
      denied: ['이 과제의 결과를 볼 수 없어요', '과제 목록에서 다시 확인해 주세요.'],
      account: ['계정 상태를 확인하고 있어요', null],
      notfound: ['결과를 찾을 수 없어요', '과제 목록에서 다시 열어 주세요.'],
      notsubmitted: ['아직 제출하지 않은 풀이예요', '제출한 뒤에 결과를 볼 수 있어요.'],
    };
    const [t, b] = S.phase === 'ok' ? msg.loading : msg[S.phase];
    main.append(h('section', { class: 'rstat', 'data-k': S.phase === 'error' ? 'error' : 'pending', 'data-testid': 'rstat', 'data-state': S.phase, role: S.phase === 'error' ? 'alert' : 'status' },
      h('h2', { 'data-testid': 'rstat-title' }, t), b !== null ? h('p', { 'data-testid': 'rstat-body' }, b) : null,
      S.phase === 'error' ? h('div', { class: 'rcheck' }, h('button', { class: 'secondary', 'data-testid': 'result-retry', disabled: S.busy, onclick: H.refresh }, S.busy ? '확인하는 중' : '다시 확인')) : null));
    side.append(...backButtons().filter((x): x is HTMLButtonElement => x !== null));
    return root;
  }

  const g = S.grading;
  const sum = summarize(g);
  if (S.changed.size > 0) {
    const names = g.points.filter((p) => S.changed.has(p.pointId)).map((p) => `${p.displayNumber}번`).join(', ');
    main.append(h('div', { class: 'band', 'data-k': 'info', role: 'status', 'data-testid': 'changed' }, `지난번 확인 뒤 결과가 바뀐 지점이 있어요: ${names} 지점`));
  }
  const sub: Record<typeof sum.kind, string> = {
    pending: '제출은 끝났어요. 결과가 나온 지점부터 이 화면에서 맞음·틀림을 볼 수 있어요.',
    partial: `결과가 나온 지점 ${sum.correct + sum.incorrect}개만 맞음·틀림을 보여요. 결과 대기 지점은 맞음·틀림에 세지 않아요.`,
    complete: '지점마다 고른 보기와 맞음·틀림을 보여 줘요.',
    none: '일반 객관식 결과는 아직 제공하지 않아요.',
  };
  const graded = sum.correct + sum.incorrect;
  main.append(h('section', { class: 'rstat', 'data-k': sum.kind, 'data-testid': 'rstat', 'data-state': sum.kind },
    h('h2', { 'data-testid': 'rstat-title' }, RESULT_HEADLINE[sum.kind]), h('p', {}, sub[sum.kind]),
    sum.total > 0 ? h('div', { class: 'rcount', 'data-testid': 'rcount' },
      h('span', { 'data-testid': 'count-total' }, `GR 지점 ${sum.total}개`),
      graded > 0 ? h('span', { 'data-r': sum.correct > 0 ? 'correct' : null, 'data-testid': 'count-correct' }, `맞음 ${sum.correct}`) : null,
      graded > 0 ? h('span', { 'data-r': sum.incorrect > 0 ? 'incorrect' : null, 'data-testid': 'count-incorrect' }, `틀림 ${sum.incorrect}`) : null,
      sum.pending > 0 ? h('span', { 'data-r': 'pending', 'data-testid': 'count-pending' }, `결과 대기 ${sum.pending}`) : null) : null,
    h('div', { class: 'rcheck' },
      h('span', { 'data-testid': 'checked-at' }, S.checkedAt !== null ? `확인한 시각 ${hhmm(S.checkedAt)} (이 기기 시계)` : ''),
      h('button', { class: 'secondary', 'data-testid': 'result-refresh', disabled: S.busy, onclick: H.refresh }, S.busy ? '확인하는 중' : '결과 다시 확인')),
    // 긴 지문 맨 아래에만 있으면 찾기 어려워 요약 영역에도 같은 진입 단추를 둔다(같은 화면으로 이동, 근거 문제와 별개)
    g.points.length > 0 && (H.toDetail !== undefined || H.toReview !== undefined) ? h('div', { class: 'rcheck' },
      H.toDetail !== undefined ? h('button', { class: 'primary', 'data-testid': 'open-detail-top', onclick: H.toDetail }, '정답·해설 보기') : null,
      H.toReview !== undefined ? h('button', { class: 'secondary', 'data-testid': 'open-review-top', onclick: H.toReview }, '오답·근거 다시 풀기') : null) : null));

  // 문장 속 결과: 문항 응답의 segments 순서, 지점 ID로 연결. 문항을 못 받았거나 지점이 맞지 않으면 목록만
  const join = S.items !== null ? joinPoints(S.items, g) : null;
  const inline = join !== null && join.matched && !S.itemsFailed;
  const byId = new Map(g.points.map((p) => [p.pointId, p]));
  if (inline) {
    for (const it of S.items!.items.filter((i) => i.responseMode === 'gr-points').sort((x, y) => x.order - y.order)) {
      const para = h('p', { class: 'rgr', 'data-testid': `rgr-${it.id}` });
      for (const s of it.segments ?? []) {
        if (s.kind === 'text') { para.append(s.text); continue; }
        const p = byId.get(s.pointId);
        if (p === undefined) continue;
        const m = markOf(p); const lab = join!.selectedLabel.get(p.pointId) ?? null;
        para.append(h('span', {
          class: 'rp' + (S.changed.has(p.pointId) ? ' changed' : ''), 'data-r': m, 'data-point': p.pointId, 'data-testid': `rp-${p.pointId}`, role: 'group',
          'aria-label': `${p.displayNumber}번 지점, 고른 보기 ${lab ?? '없음'}, ${MARK_WORD[m]}`,
        }, h('span', { class: 'pt-no', 'aria-hidden': 'true' }, p.displayNumber), h('span', { class: 'sel', 'aria-hidden': 'true' }, lab ?? '고른 보기 없음'), h('span', { class: 'tag', 'aria-hidden': 'true' }, MARK_LABEL[m])));
      }
      main.append(h('section', { class: 'item' }, h('div', { class: 'item-h' }, h('span', { class: 'no' }, `${it.number}번`), h('span', { class: 'loc' }, S.items!.release.title)), para));
    }
  } else if (g.points.length > 0) {
    main.append(h('p', { class: 'notice', 'data-testid': 'items-missing' }, '문장을 불러오지 못해 지점 번호로만 보여요.'));
  }
  // DEV-I-010: 근거 문제 진입(결과를 받은 화면, GR 지점 1개 이상). GR 결과·집계는 바꾸지 않는다
  if (g.points.length > 0 && H.toEvidence !== undefined) {
    main.append(h('hr', { class: 'ev-sep' }), h('section', { class: 'ev-entry', 'data-testid': 'ev-entry' },
      h('button', { class: 'primary', 'data-testid': 'open-evidence', onclick: H.toEvidence }, '근거 문제 풀기'),
      h('p', {}, '정답 확인이나 오답 보충을 위한 근거 문제가 제공되면 풀 수 있어요. 근거 문제 결과는 위 GR 결과를 바꾸지 않아요.')));
  }
  if (g.points.length > 0 && H.toDetail !== undefined) {
    main.append(h('section', { class: 'ev-entry', 'data-testid': 'detail-entry' },
      h('button', { class: 'primary', 'data-testid': 'open-detail', onclick: H.toDetail }, '정답·해설 보기'),
      h('p', {}, '지점마다 내가 고른 답, 정답 전체, 업로드된 해설을 펼쳐 볼 수 있어요. 근거 문제 풀기와는 달라요.')));
  }
  if (g.points.length > 0 && H.toReview !== undefined) {
    main.append(h('section', { class: 'ev-entry', 'data-testid': 'review-entry' },
      h('button', { class: 'primary', 'data-testid': 'open-review', onclick: H.toReview }, '오답·근거 다시 풀기'),
      h('p', {}, '틀렸거나 근거를 아직 통과하지 못한 지점만 다시 풀어요. 업로드한 자료의 문장 속 근거 문제는 여기서 풀어요. 처음 제출한 답과 성적은 바뀌지 않아요.')));
  }
  const mc = S.items !== null ? S.items.items.filter((i) => i.responseMode !== 'gr-points').length : 0;
  if (mc > 0) main.append(h('p', { class: 'rnote', 'data-testid': 'mc-note' }, `일반 객관식 ${mc}문항은 이 화면에서 결과를 보여 주지 않아요.`));

  if (g.points.length > 0) {
    side.append(h('h3', {}, '지점별 결과'), h('ul', { class: 'rlist', 'data-testid': 'rlist' }, ...g.points.map((p) => {
      const m = markOf(p);
      const lab = inline ? (join!.selectedLabel.get(p.pointId) ?? null) : null;
      return h('li', { 'data-r': m, 'data-testid': `row-${p.pointId}`, 'data-point': p.pointId },
        h('span', { class: 'n' }, p.displayNumber), h('span', { class: 'v' }, lab ?? '—'), h('span', { class: 's' }, MARK_LABEL[m]));
    })));
  }
  side.append(h('p', { class: 'rnote' }, '정답과 해설은 위의 "정답·해설 보기"에서 펼쳐 봐요.'), ...backButtons().filter((x): x is HTMLButtonElement => x !== null));
  return root;
}
